import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
// components
import SelectDropdown from '../../../components/SelectDropdown';
import ErrorText from '../../../components/ErrorText';
import NoData from '../../../components/NoData';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';
import { CreativeListComponent } from './CreativeListComponent';
// hooks
import { useCreativeHook } from '../hooks/useCreativeHook';
// constants
import { CREATIVE_TYPE, FILTER_TYPES, MONTHS } from '../Constants';
// styles
import '../Styles/Styles.css';

const FilterDropdown = ({ condition, dropdownProps, shimmerStyle }) => {
  return condition ? (
    <SelectDropdown {...dropdownProps} />
  ) : (
    <ShimmerComponent updatedStyle={shimmerStyle} />
  );
};

FilterDropdown.propTypes = {
  condition: PropTypes.bool.isRequired,
  dropdownProps: PropTypes.shape({
    options: PropTypes.array,
    label: PropTypes.string,
    resetValues: PropTypes.bool,
    handleResetValues: PropTypes.func,
    singleSelectDefaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.string,
    handleFilterChange: PropTypes.func,
    marginExist: PropTypes.bool,
    disabled: PropTypes.bool,
  }).isRequired,
  shimmerStyle: PropTypes.object,
};

FilterDropdown.defaultProps = {
  shimmerStyle: {},
};

const NoDataDisplay = ({ error }) => {
  return (
    <Box
      className="creative-nodata-box"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
      }}
    >
      <NoData textValue={error ? 'Error loading data' : 'No data available'} />
    </Box>
  );
};

NoDataDisplay.propTypes = {
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const LoadingDisplay = () => {
  return (
    <ShimmerComponent
      updatedStyle={{
        width: '100%',
        height: '100%',
        backgroundSize: '2000px 800px',
        border: '0px',
      }}
    >
      <BouncingLogo height="72vh" text="Loding..." />
    </ShimmerComponent>
  );
};

const ContentDisplay = ({ data }) => {
  return (
    <div style={{ width: '100%', margin: 'auto', padding: '24px 0px' }}>
      <span className="creative-section-title">Files</span>
      <CreativeListComponent data={data} />
    </div>
  );
};

ContentDisplay.propTypes = {
  data: PropTypes.array.isRequired,
};

const NoBranchesError = () => {
  return (
    <div style={{ height: '88vh', overflow: 'scroll', padding: '24px', paddingBottom: '100px' }}>
      <div style={{ width: 'fitContent', marginBottom: '55px' }}>
        <div
          style={{
            width: '90%',
            textAlign: 'left',
            margin: 'auto',
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.6',
          }}
        >
          <ErrorText reason="No branches assigned to user" />
        </div>
      </div>
    </div>
  );
};

const Creatives = (_) => {
  const {
    options,
    userStores,
    isLoading,
    error,
    handleResetForFilters,
    resetFilter,
    yearsList,
    currentMonth,
    initialLoading,
    handleFilterChange,
    creativeData,
    urlMonth,
    urlYear,
    urlStoreId,
    urlCreativeType,
  } = useCreativeHook();

  if (userStores.length === 0) {
    return <NoBranchesError />;
  }

  const isInitiallyLoaded = !initialLoading?.current;
  const hasData = creativeData?.data?.length > 0;
  const showNoData = (error || !hasData) && !isLoading && isInitiallyLoaded;
  const showLoading = isLoading || !isInitiallyLoaded;

  const monthDefaultValue = urlMonth
    ? MONTHS[parseInt(urlMonth) - 1]?.value
    : MONTHS[currentMonth?.current - 1]?.value;

  return (
    <div style={{ height: '88vh', overflow: 'scroll', padding: '24px', paddingBottom: '100px' }}>
      <div style={{ width: 'fitContent', marginBottom: '24px' }}>
        <div className="creative-page-title">Creatives</div>
      </div>
      <div
        className="creative-filter-bar"
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: '13px',
          width: '100%',
          margin: 'auto',
        }}
      >
        <div
          style={{
            marginRight: 'auto',
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
          }}
        >
          <FilterDropdown
            condition={isInitiallyLoaded}
            dropdownProps={{
              options: options,
              label: 'Select Branch',
              resetValues: resetFilter,
              handleResetValues: handleResetForFilters,
              singleSelectDefaultValue: parseInt(urlStoreId) || options[0]?.value,
              width: '200px',
              handleFilterChange: (e) => handleFilterChange(e, FILTER_TYPES.BRANCH),
              marginExist: false,
              disabled: isLoading,
            }}
            shimmerStyle={{ width: '200px' }}
          />
          <FilterDropdown
            condition={isInitiallyLoaded}
            dropdownProps={{
              options: yearsList?.current || [],
              label: 'Select Year',
              resetValues: resetFilter,
              handleResetValues: handleResetForFilters,
              singleSelectDefaultValue: parseInt(urlYear) || yearsList?.current?.[0]?.value,
              width: '100px',
              handleFilterChange: (e) => handleFilterChange(e, FILTER_TYPES.YEAR),
              marginExist: false,
              disabled: isLoading,
            }}
          />

          <FilterDropdown
            condition={isInitiallyLoaded}
            dropdownProps={{
              options: MONTHS,
              label: 'Select Month',
              resetValues: resetFilter,
              handleResetValues: handleResetForFilters,
              singleSelectDefaultValue: monthDefaultValue,
              width: '100px',
              handleFilterChange: (e) => handleFilterChange(e, FILTER_TYPES.MONTH),
              marginExist: false,
              disabled: isLoading,
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <FilterDropdown
            condition={isInitiallyLoaded}
            dropdownProps={{
              options: CREATIVE_TYPE,
              label: 'Select Type',
              resetValues: resetFilter,
              handleResetValues: handleResetForFilters,
              singleSelectDefaultValue: urlCreativeType || CREATIVE_TYPE[0]?.value,
              width: '150px',
              handleFilterChange: (e) => handleFilterChange(e, FILTER_TYPES.CREATIVE_TYPE),
              marginExist: false,
              disabled: isLoading,
            }}
            shimmerStyle={{ width: '150px' }}
          />
        </div>
      </div>

      {showNoData && <NoDataDisplay error={error} />}

      {showLoading ? <LoadingDisplay /> : hasData && <ContentDisplay data={creativeData.data} />}
    </div>
  );
};

export default Creatives;
